import { sql } from "drizzle-orm";
import {
  deserializeWorkflowValue,
  getWorkflowDef,
  loadWorkflowSnapshot,
  parseWorkflowDurationMs,
  serializeWorkflowValue,
} from "@gencow/core";
import type { GencowCtx, WorkflowCtx, WorkflowDuration } from "@gencow/core";
import {
  claimWorkflowEvent,
  claimWorkflowRun,
  getWorkflowRow,
  getWorkflowStep,
  listRecoverableWorkflows,
  markWorkflowCompleted,
  markWorkflowFailed,
  markWorkflowHandlerStarted,
  markWorkflowPendingAtStep,
  requeueWorkflowBeforeFirstStep,
  markWorkflowSleepStepCompleted,
  markWorkflowSleepStepPending,
  markWorkflowWaitStepCompleted,
  markWorkflowWaitStepFailed,
  markWorkflowWaitStepPending,
  requeueWorkflow,
} from "./workflow-runner-state.js";
import type { WorkflowSleepState, WorkflowWaitState } from "./workflow-runner-state.js";
import { attachWorkflowDocumentServices } from "./server-document-runtime.js";
import { providerRateLimitRetrySignal } from "./provider-rate-limit-retry.js";
import { computeWorkflowTimeoutDecision } from "./workflow-runtime-contract.js";
import { WORKFLOW_V2_SYSTEM_SCHEMA_STATEMENTS } from "./workflow-kernel-v2-store.js";
import type { WorkflowV2CapacityPolicy } from "./workflow-kernel-v2-capacity.js";

type DbLike = {
  execute: (query: ReturnType<typeof sql>) => Promise<unknown>;
};

type LoggerLike = Pick<Console, "log" | "warn" | "error">;
type WorkflowLogLevel = keyof LoggerLike;

export type ResumeWorkflowResult =
  | { status: "not_found"; workflowId: string }
  | { status: "skipped"; workflowId: string; workflowName: string }
  | { status: "sleeping"; workflowId: string; workflowName: string; stepName: string; nextDelayMs: number }
  | { status: "waiting"; workflowId: string; workflowName: string; stepName: string; eventName: string }
  | { status: "completed"; workflowId: string; workflowName: string }
  | {
      status: "requeued";
      workflowId: string;
      workflowName: string;
      retryCount: number;
      nextDelayMs: number;
      errorCode?: string;
    }
  | {
      status: "failed";
      workflowId: string;
      workflowName: string;
      retryCount: number;
      error: string;
      errorCode?: string;
    };

export interface ResumeWorkflowOptions {
  db: DbLike;
  workflowId: string;
  buildCtx: () => GencowCtx;
  scheduleResume: (workflowId: string, delayMs: number) => string;
  capacityPolicy?: WorkflowV2CapacityPolicy;
  logger?: LoggerLike;
  now?: () => number;
}

export const WORKFLOW_RUNNING_STALE_AFTER_MS = 15 * 60 * 1000;
const WORKFLOW_SLEEP_STEP_PREFIX = "sleep";
const WORKFLOW_WAIT_STEP_PREFIX = "wait";
const WORKFLOW_PARALLEL_STEP_PREFIX = "parallel";

export const WORKFLOW_SYSTEM_SCHEMA_STATEMENTS = [
  `CREATE TABLE IF NOT EXISTS _gencow_workflows (
        id TEXT PRIMARY KEY,
        name TEXT NOT NULL,
        args JSONB NOT NULL DEFAULT '{}'::jsonb,
        status TEXT NOT NULL DEFAULT 'pending',
        current_step TEXT,
        result JSONB,
        error TEXT,
        error_code TEXT,
        realtime_token TEXT NOT NULL DEFAULT '',
        retry_count INTEGER NOT NULL DEFAULT 0,
        max_retries INTEGER NOT NULL DEFAULT 3,
        max_duration_ms BIGINT NOT NULL DEFAULT 1800000,
        first_claimed_at TIMESTAMPTZ,
        handler_started_at TIMESTAMPTZ,
        last_resume_attempt_at TIMESTAMPTZ,
        runnable_at TIMESTAMPTZ,
        started_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        completed_at TIMESTAMPTZ,
        user_id TEXT
    )`,
  `ALTER TABLE _gencow_workflows ADD COLUMN IF NOT EXISTS realtime_token TEXT NOT NULL DEFAULT ''`,
  `ALTER TABLE _gencow_workflows ADD COLUMN IF NOT EXISTS error_code TEXT`,
  `ALTER TABLE _gencow_workflows ADD COLUMN IF NOT EXISTS first_claimed_at TIMESTAMPTZ`,
  `ALTER TABLE _gencow_workflows ADD COLUMN IF NOT EXISTS handler_started_at TIMESTAMPTZ`,
  `ALTER TABLE _gencow_workflows ADD COLUMN IF NOT EXISTS last_resume_attempt_at TIMESTAMPTZ`,
  `ALTER TABLE _gencow_workflows ADD COLUMN IF NOT EXISTS runnable_at TIMESTAMPTZ`,
  `DO $$
    BEGIN
        IF EXISTS (
            SELECT 1
            FROM information_schema.columns
            WHERE table_name = '_gencow_workflows'
              AND column_name = 'app_id'
        ) THEN
            EXECUTE 'ALTER TABLE _gencow_workflows ALTER COLUMN app_id DROP NOT NULL';
        END IF;
    END $$`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflows_status ON _gencow_workflows (status, started_at DESC)`,
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_steps (
        id SERIAL PRIMARY KEY,
        workflow_id TEXT NOT NULL REFERENCES _gencow_workflows(id) ON DELETE CASCADE,
        step_name TEXT NOT NULL,
        status TEXT NOT NULL DEFAULT 'pending',
        output JSONB,
        error TEXT,
        started_at TIMESTAMPTZ,
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        completed_at TIMESTAMPTZ,
        UNIQUE(workflow_id, step_name)
    )`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_steps_lookup ON _gencow_workflow_steps (workflow_id, step_name)`,
  `CREATE TABLE IF NOT EXISTS _gencow_workflow_events (
        id TEXT PRIMARY KEY,
        workflow_id TEXT NOT NULL REFERENCES _gencow_workflows(id) ON DELETE CASCADE,
        event_name TEXT NOT NULL,
        payload JSONB,
        received_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        consumed_at TIMESTAMPTZ
    )`,
  `ALTER TABLE _gencow_workflow_events ADD COLUMN IF NOT EXISTS received_at TIMESTAMPTZ`,
  `ALTER TABLE _gencow_workflow_events ADD COLUMN IF NOT EXISTS consumed_at TIMESTAMPTZ`,
  `DO $$
    BEGIN
        IF EXISTS (
            SELECT 1
            FROM information_schema.columns
            WHERE table_name = '_gencow_workflow_events'
              AND column_name = 'created_at'
        ) THEN
            EXECUTE '
                UPDATE _gencow_workflow_events
                SET received_at = COALESCE(received_at, created_at)
                WHERE received_at IS NULL
            ';
        END IF;
    END $$`,
  `UPDATE _gencow_workflow_events
    SET received_at = NOW()
    WHERE received_at IS NULL`,
  `ALTER TABLE _gencow_workflow_events ALTER COLUMN received_at SET NOT NULL`,
  `ALTER TABLE _gencow_workflow_events ALTER COLUMN received_at SET DEFAULT NOW()`,
  `DO $$
    BEGIN
        IF EXISTS (
            SELECT 1
            FROM information_schema.columns
            WHERE table_name = '_gencow_workflow_events'
              AND column_name = 'id'
              AND data_type <> 'text'
        ) THEN
            EXECUTE 'ALTER TABLE _gencow_workflow_events ALTER COLUMN id DROP DEFAULT';
            EXECUTE 'ALTER TABLE _gencow_workflow_events ALTER COLUMN id TYPE TEXT USING id::text';
        END IF;
    END $$`,
  `ALTER TABLE _gencow_workflow_events ALTER COLUMN id SET DEFAULT gen_random_uuid()::text`,
  `ALTER TABLE _gencow_workflow_events DROP CONSTRAINT IF EXISTS _gencow_workflow_events_workflow_id_event_name_key`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_workflow_events_lookup ON _gencow_workflow_events (workflow_id, event_name, received_at)`,
  ...WORKFLOW_V2_SYSTEM_SCHEMA_STATEMENTS,
  `CREATE TABLE IF NOT EXISTS _gencow_job_admission_buckets (
        scope TEXT NOT NULL,
        bucket_start TIMESTAMPTZ NOT NULL,
        count INTEGER NOT NULL DEFAULT 0,
        updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
        PRIMARY KEY (scope, bucket_start)
    )`,
  `CREATE INDEX IF NOT EXISTS idx__gencow_job_admission_buckets_updated ON _gencow_job_admission_buckets (updated_at)`,
] as const;

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function parseJsonField(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function parseWorkflowDate(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function restoreWorkflowAuth(baseAuth: GencowCtx["auth"], workflowUserId: string | null): GencowCtx["auth"] {
  if (baseAuth.getUserIdentity()) {
    return baseAuth;
  }
  if (!workflowUserId) {
    return baseAuth;
  }

  const restoredUser = {
    id: workflowUserId,
    email: `${workflowUserId}@workflow.local`,
  };

  return {
    getUserIdentity: () => restoredUser,
    requireAuth: () => restoredUser,
  };
}

function formatDurationMs(durationMs: number): string {
  if (!Number.isFinite(durationMs) || durationMs < 0) return "0ms";
  if (durationMs < 1_000) return `${Math.round(durationMs)}ms`;
  if (durationMs < 60_000) return `${(durationMs / 1_000).toFixed(1)}s`;
  return `${Math.round(durationMs / 60_000)}m`;
}

function buildPayloadPreview(value: unknown, maxLength = 180): string | null {
  if (value == null) return null;
  try {
    const serialized = JSON.stringify(value);
    if (!serialized) return null;
    return serialized.length > maxLength ? `${serialized.slice(0, maxLength - 3)}...` : serialized;
  } catch {
    const raw = String(value);
    return raw.length > maxLength ? `${raw.slice(0, maxLength - 3)}...` : raw;
  }
}

function writeWorkflowLog(
  logger: LoggerLike,
  level: WorkflowLogLevel,
  message: string,
  options?: { verbose?: boolean },
): void {
  const prefix = options?.verbose ? "[workflow:verbose]" : "[workflow]";
  logger[level](`${prefix} ${message}`);
}

function getWorkflowSleepStepName(index: number): string {
  return `${WORKFLOW_SLEEP_STEP_PREFIX}#${index}`;
}

function getWorkflowWaitStepName(eventName: string, index: number): string {
  return `${WORKFLOW_WAIT_STEP_PREFIX}:${eventName}#${index}`;
}

function getWorkflowParallelStepName(groupIndex: number, branchIndex: number): string {
  return `${WORKFLOW_PARALLEL_STEP_PREFIX}#${groupIndex}:${branchIndex}`;
}

function isWorkflowSleepState(value: unknown): value is WorkflowSleepState {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as WorkflowSleepState).wakeAt === "string" &&
    typeof (value as WorkflowSleepState).durationMs === "number"
  );
}

function isWorkflowWaitState(value: unknown): value is WorkflowWaitState {
  if (!value || typeof value !== "object") {
    return false;
  }
  const waitState = value as Partial<WorkflowWaitState>;
  return (
    typeof waitState.eventName === "string" &&
    (waitState.timeoutAt == null || typeof waitState.timeoutAt === "string") &&
    (waitState.timeoutMs == null || typeof waitState.timeoutMs === "number")
  );
}

function createWorkflowSleepState(nowMs: number, durationMs: number): WorkflowSleepState {
  return {
    wakeAt: new Date(nowMs + durationMs).toISOString(),
    durationMs,
  };
}

function createWorkflowWaitState(
  nowMs: number,
  eventName: string,
  timeout: WorkflowDuration | undefined,
): WorkflowWaitState {
  if (timeout == null) {
    return {
      eventName,
      timeoutAt: null,
      timeoutMs: null,
    };
  }

  const timeoutMs = parseWorkflowDurationMs(timeout, "wf.waitForEvent() timeout");
  return {
    eventName,
    timeoutAt: new Date(nowMs + timeoutMs).toISOString(),
    timeoutMs,
  };
}

class WorkflowSleepSignal extends Error {
  readonly stepName: string;
  readonly nextDelayMs: number;

  constructor(stepName: string, nextDelayMs: number) {
    super(`workflow sleep checkpoint scheduled for ${stepName}`);
    this.name = "WorkflowSleepSignal";
    this.stepName = stepName;
    this.nextDelayMs = Math.max(0, Math.floor(nextDelayMs));
  }
}

class WorkflowWaitSignal extends Error {
  readonly stepName: string;
  readonly eventName: string;
  readonly nextDelayMs: number | null;

  constructor(stepName: string, eventName: string, nextDelayMs: number | null = null) {
    super(`workflow waiting for event "${eventName}" at ${stepName}`);
    this.name = "WorkflowWaitSignal";
    this.stepName = stepName;
    this.eventName = eventName;
    this.nextDelayMs = nextDelayMs == null ? null : Math.max(0, Math.floor(nextDelayMs));
  }
}

class WorkflowTimeoutError extends Error {
  readonly stepName: string;
  readonly eventName: string;

  constructor(stepName: string, eventName: string, timeoutMs: number | null) {
    super(
      timeoutMs == null
        ? `workflow waitForEvent("${eventName}") timed out at ${stepName}`
        : `workflow waitForEvent("${eventName}") timed out after ${timeoutMs}ms at ${stepName}`,
    );
    this.name = "WorkflowTimeoutError";
    this.stepName = stepName;
    this.eventName = eventName;
  }
}

async function emitWorkflowSnapshot(
  realtime: GencowCtx["realtime"] | undefined,
  db: DbLike,
  workflowId: string,
  logger: LoggerLike,
): Promise<void> {
  if (!realtime || typeof realtime.emit !== "function") return;

  try {
    const snapshot = await loadWorkflowSnapshot(db as any, workflowId);
    if (!snapshot) return;
    realtime.emit(snapshot.realtimeKey, snapshot);
  } catch (error) {
    logger.warn(`[workflow] failed to emit realtime snapshot for ${workflowId}: ${errorMessage(error)}`);
  }
}

export function computeWorkflowRetryDelayMs(nextRetryCount: number): number {
  return 30_000 * 3 ** Math.max(0, nextRetryCount - 1);
}

export { listRecoverableWorkflows } from "./workflow-runner-state.js";

export async function requeueRecoverableWorkflowRuns(options: {
  db: DbLike;
  workflowNames: Iterable<string>;
  scheduleResume: (workflowName: string, workflowId: string, delayMs: number) => void;
}): Promise<Array<{ id: string; name: string }>> {
  const allowedNames = new Set(options.workflowNames);
  if (allowedNames.size === 0) return [];

  const recoverable = (await listRecoverableWorkflows(options.db)).filter((row) =>
    allowedNames.has(row.name),
  );

  for (const row of recoverable) {
    options.scheduleResume(row.name, row.id, row.retryDelayMs);
  }

  return recoverable.map(({ id, name }) => ({ id, name }));
}

export async function resumeWorkflowRun(options: ResumeWorkflowOptions): Promise<ResumeWorkflowResult> {
  const logger = options.logger ?? console;
  const now = options.now ?? Date.now;

  const claimed = await claimWorkflowRun(options.db, options.workflowId);
  if (!claimed) {
    const existing = await getWorkflowRow(options.db, options.workflowId);
    if (!existing) {
      return { status: "not_found", workflowId: options.workflowId };
    }
    if (existing.status === "completed") {
      return {
        status: "completed",
        workflowId: existing.id,
        workflowName: existing.name,
      };
    }
    return {
      status: "skipped",
      workflowId: existing.id,
      workflowName: existing.name,
    };
  }

  const workflowDef = getWorkflowDef(claimed.name);
  if (!workflowDef) {
    const missingError = `workflow "${claimed.name}" is not registered in this runtime`;
    await markWorkflowFailed(
      options.db,
      claimed.id,
      missingError,
      claimed.retry_count,
      "workflow_definition_missing",
    );
    await emitWorkflowSnapshot(options.buildCtx().realtime, options.db, claimed.id, logger);
    return {
      status: "failed",
      workflowId: claimed.id,
      workflowName: claimed.name,
      retryCount: claimed.retry_count,
      errorCode: "workflow_definition_missing",
      error: missingError,
    };
  }

  const handlerStartedAt = claimed.handler_started_at ?? new Date(now()).toISOString();
  if (!claimed.handler_started_at) {
    await markWorkflowHandlerStarted(options.db, claimed.id, handlerStartedAt);
  }
  const handlerStartedAtMs = parseWorkflowDate(handlerStartedAt) ?? now();
  const timeoutDecision = computeWorkflowTimeoutDecision(
    { ...claimed, handler_started_at: handlerStartedAt },
    now(),
  );
  if (timeoutDecision) {
    const nextRetry = claimed.retry_count + 1;
    if (timeoutDecision.retryable && nextRetry <= claimed.max_retries) {
      const nextDelayMs = computeWorkflowRetryDelayMs(nextRetry);
      await requeueWorkflowBeforeFirstStep(
        options.db,
        claimed.id,
        nextRetry,
        timeoutDecision.error,
        timeoutDecision.errorCode,
      );
      await emitWorkflowSnapshot(options.buildCtx().realtime, options.db, claimed.id, logger);
      options.scheduleResume(claimed.id, nextDelayMs);
      writeWorkflowLog(
        logger,
        "warn",
        `${claimed.name} failed before first step — retry ${nextRetry}/${claimed.max_retries} in ${nextDelayMs}ms (id: ${claimed.id}): ${timeoutDecision.error}`,
      );
      return {
        status: "requeued",
        workflowId: claimed.id,
        workflowName: claimed.name,
        retryCount: nextRetry,
        nextDelayMs,
        errorCode: timeoutDecision.errorCode,
      };
    }

    await markWorkflowFailed(
      options.db,
      claimed.id,
      timeoutDecision.error,
      timeoutDecision.retryable ? nextRetry : claimed.retry_count,
      timeoutDecision.errorCode,
    );
    await emitWorkflowSnapshot(options.buildCtx().realtime, options.db, claimed.id, logger);
    writeWorkflowLog(
      logger,
      "error",
      `${claimed.name} failed maxDuration check (id: ${claimed.id}): ${timeoutDecision.error}`,
    );
    return {
      status: "failed",
      workflowId: claimed.id,
      workflowName: claimed.name,
      retryCount: timeoutDecision.retryable ? nextRetry : claimed.retry_count,
      errorCode: timeoutDecision.errorCode,
      error: timeoutDecision.error,
    };
  }

  const baseCtx = options.buildCtx();
  const workflowArgs = deserializeWorkflowValue(parseJsonField(claimed.args));
  await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
  const argsPreview = buildPayloadPreview(workflowArgs);
  writeWorkflowLog(logger, "log", `${claimed.name} resumed (id: ${claimed.id})`);
  writeWorkflowLog(
    logger,
    "log",
    `${claimed.name} resume context (id: ${claimed.id}, retry: ${claimed.retry_count}/${claimed.max_retries}, from: ${claimed.current_step ?? "start"}${argsPreview ? `, args=${argsPreview}` : ""})`,
    { verbose: true },
  );
  let sleepIndex = 0;
  let waitIndex = 0;
  let parallelIndex = 0;

  const runWorkflowStep = async <TResult>(
    stepName: string,
    run: () => Promise<TResult>,
  ): Promise<TResult> => {
    const existing = await getWorkflowStep(options.db, claimed.id, stepName);
    if (existing?.status === "completed") {
      writeWorkflowLog(logger, "log", `${claimed.name} step ${stepName} checkpoint hit (id: ${claimed.id})`, {
        verbose: true,
      });
      return deserializeWorkflowValue(parseJsonField(existing.output)) as TResult;
    }

    const stepStartedAtMs = now();
    writeWorkflowLog(logger, "log", `${claimed.name} step ${stepName} started (id: ${claimed.id})`);

    await options.db.execute(sql`
            INSERT INTO _gencow_workflow_steps (
                workflow_id,
                step_name,
                status,
                output,
                error,
                started_at,
                updated_at,
                completed_at
            )
            VALUES (
                ${claimed.id},
                ${stepName},
                'running',
                NULL,
                NULL,
                NOW(),
                NOW(),
                NULL
            )
            ON CONFLICT (workflow_id, step_name)
            DO UPDATE SET
                status = 'running',
                output = NULL,
                error = NULL,
                started_at = NOW(),
                updated_at = NOW(),
                completed_at = NULL
        `);

    await options.db.execute(sql`
            UPDATE _gencow_workflows
            SET
                current_step = ${stepName},
                updated_at = NOW(),
                error = NULL
            WHERE id = ${claimed.id}
        `);
    await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);

    try {
      const output = await run();
      const persisted = serializeWorkflowValue(output);
      await options.db.execute(sql`
                UPDATE _gencow_workflow_steps
                SET
                    status = 'completed',
                    output = ${JSON.stringify(persisted)}::jsonb,
                    error = NULL,
                    updated_at = NOW(),
                    completed_at = NOW()
                WHERE workflow_id = ${claimed.id}
                  AND step_name = ${stepName}
            `);
      await options.db.execute(sql`
                UPDATE _gencow_workflows
                SET
                    current_step = ${stepName},
                    updated_at = NOW()
                WHERE id = ${claimed.id}
            `);
      await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
      writeWorkflowLog(
        logger,
        "log",
        `${claimed.name} step ${stepName} completed in ${formatDurationMs(now() - stepStartedAtMs)} (id: ${claimed.id})`,
      );
      return output;
    } catch (error) {
      const message = errorMessage(error);
      await options.db.execute(sql`
                UPDATE _gencow_workflow_steps
                SET
                    status = 'failed',
                    error = ${message},
                    updated_at = NOW(),
                    completed_at = NULL
                WHERE workflow_id = ${claimed.id}
                  AND step_name = ${stepName}
            `);
      await options.db.execute(sql`
                UPDATE _gencow_workflows
                SET
                    current_step = ${stepName},
                    error = ${message},
                    updated_at = NOW()
                WHERE id = ${claimed.id}
            `);
      await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
      writeWorkflowLog(
        logger,
        "error",
        `${claimed.name} step ${stepName} failed after ${formatDurationMs(now() - stepStartedAtMs)} (id: ${claimed.id}): ${message}`,
      );
      throw error;
    }
  };

  const workflowAuth = restoreWorkflowAuth(baseCtx.auth, claimed.user_id);
  const workflowCtx: WorkflowCtx = {
    ...baseCtx,
    auth: workflowAuth,
    workflowId: claimed.id,
    workflowName: claimed.name,
    services: attachWorkflowDocumentServices(baseCtx.services, {
      workflowId: claimed.id,
      workflowName: claimed.name,
      auth: workflowAuth,
    }),
    step: async <TResult>(stepName: string, run: () => Promise<TResult>): Promise<TResult> => {
      return runWorkflowStep(stepName, run);
    },
    sleep: async (duration) => {
      const stepName = getWorkflowSleepStepName(++sleepIndex);
      const existing = await getWorkflowStep(options.db, claimed.id, stepName);
      if (existing?.status === "completed") {
        return;
      }

      const existingSleepState = existing ? deserializeWorkflowValue(parseJsonField(existing.output)) : null;
      if (existingSleepState != null && !isWorkflowSleepState(existingSleepState)) {
        throw new Error(`workflow sleep checkpoint "${stepName}" is missing wake metadata`);
      }

      const sleepState =
        existingSleepState && isWorkflowSleepState(existingSleepState)
          ? existingSleepState
          : createWorkflowSleepState(now(), parseWorkflowDurationMs(duration, "wf.sleep()"));
      const wakeAtMs = parseWorkflowDate(sleepState.wakeAt);
      if (wakeAtMs == null) {
        throw new Error(`workflow sleep checkpoint "${stepName}" has invalid wakeAt "${sleepState.wakeAt}"`);
      }

      if (wakeAtMs <= now()) {
        await markWorkflowSleepStepCompleted(options.db, claimed.id, stepName, sleepState);
        return;
      }

      await markWorkflowSleepStepPending(options.db, claimed.id, stepName, sleepState);
      throw new WorkflowSleepSignal(stepName, wakeAtMs - now());
    },
    waitForEvent: async <TPayload = unknown>(
      eventName: string,
      timeout?: WorkflowDuration,
    ): Promise<TPayload> => {
      const normalizedEventName = eventName.trim();
      if (!normalizedEventName) {
        throw new Error("wf.waitForEvent() requires a non-empty event name");
      }

      const stepName = getWorkflowWaitStepName(normalizedEventName, ++waitIndex);
      const existing = await getWorkflowStep(options.db, claimed.id, stepName);
      if (existing?.status === "completed") {
        return deserializeWorkflowValue(parseJsonField(existing.output)) as TPayload;
      }

      const existingWaitState = existing ? deserializeWorkflowValue(parseJsonField(existing.output)) : null;
      if (existingWaitState != null && !isWorkflowWaitState(existingWaitState)) {
        throw new Error(`workflow wait checkpoint "${stepName}" is missing event metadata`);
      }

      const waitState =
        existingWaitState && isWorkflowWaitState(existingWaitState)
          ? {
              eventName: existingWaitState.eventName,
              timeoutAt: existingWaitState.timeoutAt ?? null,
              timeoutMs: existingWaitState.timeoutMs ?? null,
            }
          : createWorkflowWaitState(now(), normalizedEventName, timeout);

      const eventRow = await claimWorkflowEvent(options.db, claimed.id, normalizedEventName);
      if (eventRow) {
        const payload = deserializeWorkflowValue(parseJsonField(eventRow.payload)) as TPayload;
        await markWorkflowWaitStepCompleted(options.db, claimed.id, stepName, payload);
        writeWorkflowLog(
          logger,
          "log",
          `${claimed.name} consumed event "${normalizedEventName}" at ${stepName} (id: ${claimed.id})`,
          { verbose: true },
        );
        return payload;
      }

      const timeoutAtMs = parseWorkflowDate(waitState.timeoutAt);
      if (timeoutAtMs != null && timeoutAtMs <= now()) {
        const timeoutError = new WorkflowTimeoutError(stepName, normalizedEventName, waitState.timeoutMs);
        await markWorkflowWaitStepFailed(options.db, claimed.id, stepName, timeoutError.message);
        throw timeoutError;
      }

      await markWorkflowWaitStepPending(options.db, claimed.id, stepName, waitState);
      throw new WorkflowWaitSignal(
        stepName,
        normalizedEventName,
        timeoutAtMs == null ? null : timeoutAtMs - now(),
      );
    },
    parallel: async <TTasks extends ReadonlyArray<() => Promise<any>>>(
      tasks: TTasks,
    ): Promise<{ [K in keyof TTasks]: Awaited<ReturnType<TTasks[K]>> }> => {
      if (tasks.length === 0) {
        return [] as { [K in keyof TTasks]: Awaited<ReturnType<TTasks[K]>> };
      }

      const groupIndex = ++parallelIndex;
      const settled = await Promise.allSettled(
        tasks.map((task, index) => runWorkflowStep(getWorkflowParallelStepName(groupIndex, index + 1), task)),
      );
      const firstRejected = settled.find((result) => result.status === "rejected");
      if (firstRejected && firstRejected.status === "rejected") {
        throw firstRejected.reason;
      }

      return settled.map((result) => {
        if (result.status !== "fulfilled") {
          throw new Error("workflow parallel branch did not fulfill");
        }
        return result.value;
      }) as { [K in keyof TTasks]: Awaited<ReturnType<TTasks[K]>> };
    },
  };

  try {
    const result = await workflowDef.handler(workflowCtx, (workflowArgs ?? {}) as never);
    await markWorkflowCompleted(options.db, claimed.id, result);
    await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
    writeWorkflowLog(
      logger,
      "log",
      `${claimed.name} completed in ${formatDurationMs(now() - handlerStartedAtMs)} (id: ${claimed.id})`,
    );
    return {
      status: "completed",
      workflowId: claimed.id,
      workflowName: claimed.name,
    };
  } catch (error) {
    if (error instanceof WorkflowSleepSignal) {
      await markWorkflowPendingAtStep(options.db, claimed.id, error.stepName);
      await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
      options.scheduleResume(claimed.id, error.nextDelayMs);
      writeWorkflowLog(
        logger,
        "log",
        `${claimed.name} sleeping at ${error.stepName} for ${error.nextDelayMs}ms (id: ${claimed.id})`,
      );
      return {
        status: "sleeping",
        workflowId: claimed.id,
        workflowName: claimed.name,
        stepName: error.stepName,
        nextDelayMs: error.nextDelayMs,
      };
    }
    if (error instanceof WorkflowWaitSignal) {
      await markWorkflowPendingAtStep(options.db, claimed.id, error.stepName);
      await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
      if (error.nextDelayMs != null) {
        options.scheduleResume(claimed.id, error.nextDelayMs);
      }
      writeWorkflowLog(
        logger,
        "log",
        `${claimed.name} waiting for event "${error.eventName}" at ${error.stepName} (id: ${claimed.id})`,
      );
      return {
        status: "waiting",
        workflowId: claimed.id,
        workflowName: claimed.name,
        stepName: error.stepName,
        eventName: error.eventName,
      };
    }
    if (error instanceof WorkflowTimeoutError) {
      await markWorkflowFailed(options.db, claimed.id, error.message, claimed.retry_count);
      await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
      writeWorkflowLog(
        logger,
        "error",
        `${claimed.name} timed out while waiting for "${error.eventName}" (id: ${claimed.id})`,
      );
      return {
        status: "failed",
        workflowId: claimed.id,
        workflowName: claimed.name,
        retryCount: claimed.retry_count,
        error: error.message,
      };
    }

    const message = errorMessage(error);
    const providerRetry = providerRateLimitRetrySignal(error);
    const nextRetry = claimed.retry_count + 1;

    if (nextRetry <= claimed.max_retries) {
      const nextDelayMs = providerRetry?.retryAfterMs ?? computeWorkflowRetryDelayMs(nextRetry);
      await requeueWorkflow(
        options.db,
        claimed.id,
        nextRetry,
        message,
        providerRetry?.errorCode ?? null,
        providerRetry ? nextDelayMs : null,
      );
      await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
      options.scheduleResume(claimed.id, nextDelayMs);
      writeWorkflowLog(
        logger,
        "warn",
        `${claimed.name} failed after ${formatDurationMs(now() - handlerStartedAtMs)} — retry ${nextRetry}/${claimed.max_retries} in ${nextDelayMs}ms (id: ${claimed.id}): ${message}`,
      );
      return {
        status: "requeued",
        workflowId: claimed.id,
        workflowName: claimed.name,
        retryCount: nextRetry,
        nextDelayMs,
        ...(providerRetry ? { errorCode: providerRetry.errorCode } : {}),
      };
    }

    await markWorkflowFailed(options.db, claimed.id, message, nextRetry, providerRetry?.errorCode ?? null);
    await emitWorkflowSnapshot(baseCtx.realtime, options.db, claimed.id, logger);
    writeWorkflowLog(
      logger,
      "error",
      `${claimed.name} failed permanently after ${formatDurationMs(now() - handlerStartedAtMs)} (id: ${claimed.id}): ${message}`,
    );
    return {
      status: "failed",
      workflowId: claimed.id,
      workflowName: claimed.name,
      retryCount: nextRetry,
      error: message,
      ...(providerRetry ? { errorCode: providerRetry.errorCode } : {}),
    };
  }
}
